import { classNames } from '@/utility/className';
import Link, { LinkProps } from 'next/link';

import { cva, VariantProps } from 'class-variance-authority';
import React, { forwardRef } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import { IconType } from 'react-icons';
import { applyStyleToMultipleVariants } from './cvs';
import Tooltip from './tooltip';

type InferredVariantProps = VariantProps<typeof buttonClasses>;
type ButtonColor = NonNullable<InferredVariantProps['color']>;

type ButtonBaseProps = {
  /** Action that happens when the button is clicked */
  onClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /**Left aligned icon*/
  StartIcon?: IconType | React.ElementType;
  /**Right aligned icon */
  EndIcon?: IconType;
  shallow?: boolean;
  /**Tool tip used when icon size is set to small */
  flex?: boolean;
  /**Tool tip used when icon size is set to small */
  tooltip?: string;
  type?: 'button' | 'submit' | 'reset';
} & Omit<InferredVariantProps, 'color'> & {
    color?: ButtonColor;
  };

export type ButtonProps = ButtonBaseProps &
  (
    | (Omit<ComponentPropsWithoutRef<'a'>, 'href' | 'onClick' | 'ref'> &
        LinkProps)
    | (Omit<ComponentPropsWithoutRef<'button'>, 'onClick' | 'ref'> & {
        href?: never;
      })
  );

const PRIMARY_TEXT_CLASS = 'text-brand-button';
const PRIMARY_DISABLED_TEXT_CLASS = 'text-brand-button/60';
const PRIMARY_LOADING_TEXT_CLASS = 'text-brand-button/30';

const buttonClasses = cva(
  'flex justify-center items-center text-sm font-medium relative rounded-md transition-colors whitespace-nowrap focus:outline-none',
  {
    variants: {
      color: {
        primary: `${PRIMARY_TEXT_CLASS} bg-brand-500 `,
        secondary: 'text-gray-900 bg-gray-200 ',
        minimal: 'text-gray-900',
        destructive: 'text-white bg-red-600 border border-red-600',
        outline: 'text-gray-900 border border-gray-200 cursor-pointer',
      },
      size: {
        sm: 'px-3 py-2 leading-4 rounded-sm' /** For backwards compatibility */,
        base: 'h-10 px-3 py-2.5 w-full',
        lg: 'h-[36px] px-4 py-2.5 ',
        xl: 'h-[44px] px-6 py-3.5 ',
        icon: 'flex justify-center min-h-[36px] min-w-[36px] ',
      },
      loading: {
        true: 'cursor-wait',
      },
      disabled: {
        true: 'cursor-not-allowed',
      },
    },
    compoundVariants: [
      // Primary variants
      {
        disabled: true,
        color: 'primary',
        className: `bg-brand-200 ${PRIMARY_DISABLED_TEXT_CLASS} border border-brand-200`,
      },
      {
        loading: true,
        color: 'primary',
        className: `bg-brand-300 ${PRIMARY_LOADING_TEXT_CLASS} `,
      },
      ...applyStyleToMultipleVariants({
        disabled: [undefined, false],
        color: 'primary',
        className:
          'bg-brand-500 hover:bg-brand-400 active:bg-brand-600 focus-visible:border focus-visible:border-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
      }),
      // Secondary variants
      {
        disabled: true,
        color: 'secondary',
        className:
          'border border-gray-200 bg-opacity-30 text-gray-900/30 bg-white ',
      },
      {
        loading: true,
        color: 'secondary',
        className: 'bg-gray-100 text-gray-900/30 ',
      },
      ...applyStyleToMultipleVariants({
        disabled: [undefined, false],
        color: 'secondary',
        className:
          'border border-gray-300 hover:border-gray-400 hover:bg-gray-50 active:bg-gray-100 focus-visible:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
      }),
      {
        disabled: true,
        color: 'minimal',
        className: 'border:gray-200 bg-opacity-30 text-gray-900/30 ',
      },
      {
        loading: true,
        color: 'minimal',
        className: 'bg-gray-100 text-gray-900/30 ',
      },
      ...applyStyleToMultipleVariants({
        disabled: [undefined, false],
        color: 'minimal',
        className:
          'hover:bg-gray-100 active:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
      }),
      // Destructive variants
      {
        disabled: true,
        color: 'destructive',
        className:
          'text-red-700/50 dark:text-red-200/60 bg-red-100 dark:bg-red-900/40 border border-red-200',
      },
      {
        loading: true,
        color: 'destructive',
        className: 'text-white/80 bg-red-600/80 border border-red-600/80',
      },
      ...applyStyleToMultipleVariants({
        disabled: [false, undefined],
        color: 'destructive',
        className:
          'bg-red-600 hover:bg-red-700 active:bg-red-800 border border-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2',
      }),
      // Outline variants
      {
        disabled: true,
        color: 'outline',
        className: 'border border-gray-200 bg-opacity-30 text-gray-900/30 ',
      },
      ...applyStyleToMultipleVariants({
        disabled: [undefined, false],
        color: 'outline',
        className:
          'hover:bg-gray-100 active:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
      }),
    ],
    defaultVariants: {
      color: 'primary',
      size: 'base',
    },
  }
);

export const Button = forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  ButtonProps
>(function Button(props: ButtonProps, forwardedRef) {
  const {
    loading = false,
    color = 'primary',
    size,
    type = 'button',
    StartIcon,
    EndIcon,
    shallow,
    href,
    onClick,
    className,
    tooltip,
    disabled: disabledProp,
    // attributes propagated from `HTMLAnchorProps` or `HTMLButtonProps`
    ...passThroughProps
  } = props;
  // Buttons are **always** disabled if we're in a `loading` state
  const disabled: boolean = Boolean(disabledProp || loading);
  // If an `href`-attr is passed it's `<a>`, otherwise it's a `<button />`
  const isLink = typeof href !== 'undefined';
  const handleClick = disabled
    ? (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        event.stopPropagation();
      }
    : onClick;

  const enforcedPrimaryTextClass =
    color === 'primary'
      ? loading
        ? PRIMARY_LOADING_TEXT_CLASS
        : props.disabled
          ? PRIMARY_DISABLED_TEXT_CLASS
          : PRIMARY_TEXT_CLASS
      : undefined;

  const elementClassName = classNames(
    buttonClasses({ color, size, loading, disabled: disabledProp }),
    className,
    isLink && disabled ? 'pointer-events-none' : undefined,
    enforcedPrimaryTextClass
  );
  const buttonDisabled = disabled ? true : undefined;
  const content = (
    <>
      {StartIcon && (
        <span
          aria-hidden="true"
          className={classNames(
            'inline-flex items-center',
            size === 'icon' ? 'pr-0' : 'pr-[2px]'
          )}
        >
          <StartIcon
            aria-hidden="true"
            focusable="false"
            className={classNames(
              size === 'icon' ? 'h-[18px] w-[18px]' : 'h-4 w-4 stroke-[1.5px]'
            )}
          />
        </span>
      )}
      {props.children}
      {loading && (
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform"
          role="status"
          aria-live="polite"
          aria-label="Loading"
        >
          <svg
            className="mx-4 h-5 w-5 animate-spin text-black"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      )}
      {EndIcon && (
        <span
          aria-hidden="true"
          className={classNames(
            'inline-flex items-center',
            size === 'icon' ? 'pl-0' : 'pl-1'
          )}
        >
          <EndIcon aria-hidden="true" focusable="false" className="h-5 w-5" />
        </span>
      )}
    </>
  );

  if (href) {
    const linkElement = (
      <Link
        href={href}
        shallow={shallow && shallow}
        onClick={handleClick as LinkProps['onClick']}
        className={elementClassName}
        aria-disabled={disabled || undefined}
        tabIndex={
          disabled
            ? -1
            : ((passThroughProps as { tabIndex?: number }).tabIndex ??
              undefined)
        }
        {...(passThroughProps as Omit<LinkProps, 'href'>)}
      >
        {content}
      </Link>
    );

    return <Wrapper tooltip={tooltip}>{linkElement}</Wrapper>;
  }

  return (
    <Wrapper tooltip={tooltip}>
      <button
        ref={forwardedRef as React.Ref<HTMLButtonElement>}
        className={elementClassName}
        disabled={buttonDisabled}
        aria-disabled={buttonDisabled}
        aria-pressed={props['aria-pressed']}
        onClick={handleClick}
        type={type}
        {...(passThroughProps as Omit<
          React.ComponentPropsWithoutRef<'button'>,
          'onClick' | 'ref'
        >)}
      >
        {content}
      </button>
    </Wrapper>
  );
});

const Wrapper = ({
  children,
  tooltip,
}: {
  tooltip?: string;
  children: React.ReactNode;
}) => {
  if (!tooltip) {
    return <>{children}</>;
  }

  return <Tooltip content={tooltip}>{children}</Tooltip>;
};
export default Button;
